/**
 * Hook unificado de Empresas — substitui useClientes/useSites/useElaboradoras/
 * useExecutoras/useLaboratorios após o refator atores-e-automacao.
 *
 * Endpoints novos: /api/v1/empresas/ com filtro `?papeis=CLIENTE` (etc.)
 * Os hooks antigos viram wrappers finos por compatibilidade — ver
 * src/hooks/useClientes.ts e similares (deprecated).
 *
 * Ver GUIA_DE_MIGRACAO_API.md para detalhe endpoint a endpoint.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PaginatedResponse } from '../types'

export type PapelEmpresa = 'CLIENTE' | 'PLANEJADOR' | 'EXECUTOR' | 'LABORATORIO'

export interface Empresa {
  id: string
  razao_social: string
  nome_fantasia: string
  cnpj: string
  inscricao_estadual: string
  endereco_logradouro: string
  endereco_numero: string
  endereco_complemento: string
  endereco_bairro: string
  endereco_cidade: string
  endereco_uf: string
  endereco_cep: string
  telefone: string
  email: string
  website: string
  registro_pj_conselho: string
  registro_pj_conselho_outro: string
  registro_pj_numero: string
  registro_pj_uf: string
  papeis: PapelEmpresa[]
  grupo_economico: string | null
  is_self: boolean
  logo_url: string | null
}

export interface EmpresaFormData
  extends Omit<Empresa, 'id' | 'is_self' | 'logo_url'> {
  papeis: PapelEmpresa[]
}

export interface AreaCliente {
  id: string
  empresa: string
  empresa_nome: string
  nome: string
  codigo: string
  status: string
  responsavel_nome: string
  ramal: string
  qtd_ocupantes: number | null
  qtd_computadores: number | null
  metragem_m2: string | null
  equipamentos_count: number
}

export type AreaClienteInput = Omit<
  AreaCliente,
  'id' | 'empresa_nome' | 'equipamentos_count'
>

export interface EmpresaListParams {
  search?: string
  papeis?: PapelEmpresa
  is_self?: boolean
  grupo_economico?: string
  ordering?: string
  page?: number
  page_size?: number
}

export function useEmpresaList(params?: EmpresaListParams) {
  return useQuery({
    queryKey: ['empresas', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<Empresa>>('/empresas/', { params })
      return r.data
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useEmpresaDetalhe(id: string) {
  return useQuery({
    queryKey: ['empresas', id],
    queryFn: async () => {
      const r = await api.get<Empresa>(`/empresas/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export function useEmpresaCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: EmpresaFormData) => api.post<Empresa>('/empresas/', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] })
    },
  })
}

export function useEmpresaUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<EmpresaFormData>) =>
      api.patch<Empresa>(`/empresas/${id}/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] })
      qc.invalidateQueries({ queryKey: ['empresas', id] })
    },
  })
}

export function useEmpresaDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/empresas/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] })
    },
  })
}

export function useAreasCliente(empresaId: string) {
  return useQuery({
    queryKey: ['areas', empresaId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<AreaCliente>>('/areas/', {
        params: { empresa: empresaId, page_size: 500 },
      })
      return r.data
    },
    enabled: !!empresaId,
  })
}

export function useAreaClienteCreate(empresaId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Omit<AreaClienteInput, 'empresa'>) =>
      api.post<AreaCliente>('/areas/', { ...data, empresa: empresaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['areas', empresaId] }),
  })
}

export function useAreaClienteUpdate(empresaId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ areaId, data }: { areaId: string; data: Partial<AreaClienteInput> }) =>
      api.patch<AreaCliente>(`/areas/${areaId}/`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['areas', empresaId] }),
  })
}

export function useAreaClienteDelete(empresaId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (areaId: string) => api.delete(`/areas/${areaId}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['areas', empresaId] }),
  })
}

export function useEmpresaLogoUpload(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (arquivo: File) => {
      const fd = new FormData()
      fd.append('arquivo', arquivo)
      const r = await api.put<{ logo_url: string }>(
        `/empresas/${id}/logo/`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] })
      qc.invalidateQueries({ queryKey: ['empresas', id] })
    },
  })
}

export function useEmpresaLogoDelete(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete(`/empresas/${id}/logo/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['empresas'] })
      qc.invalidateQueries({ queryKey: ['empresas', id] })
    },
  })
}

// Filtros de conveniência por papel — substituem useClienteList, etc.
export function useClientesV2(params?: Omit<EmpresaListParams, 'papeis'>) {
  return useEmpresaList({ ...params, papeis: 'CLIENTE' })
}

export function usePlanejadoresV2(params?: Omit<EmpresaListParams, 'papeis'>) {
  return useEmpresaList({ ...params, papeis: 'PLANEJADOR' })
}

export function useExecutoresV2(params?: Omit<EmpresaListParams, 'papeis'>) {
  return useEmpresaList({ ...params, papeis: 'EXECUTOR' })
}

export function useLaboratoriosV2(params?: Omit<EmpresaListParams, 'papeis'>) {
  return useEmpresaList({ ...params, papeis: 'LABORATORIO' })
}
